import { useScroll } from 'framer-motion';
import { StackedContent, MotionImage, useSanitizedTranslation, TextBlock } from 'ui';
import { useState, useRef, useEffect, useCallback } from 'react';
import { InputType } from '../../types';
import { ModalForm } from '../forms/ModalForm';

export interface MeetTheTeamProps {
  carouselItems: MeetTheTeamCarouselItem[];
}

export interface MeetTheTeamCarouselItem {
  bgColor: string;
  cardTextColor: string;
  name: string;
  title?: string;
  awardCategory?: string;
  imageSrc?: string;
}

const MeetTheTeamCarouselItem = ({ bgColor, cardTextColor, name, title }: MeetTheTeamCarouselItem) => {
  const { t } = useSanitizedTranslation();

  return (
    <div
      className={`relative z-20 flex h-full min-h-[150px] w-max min-w-[325px] max-w-[325px] snap-center flex-col justify-center overflow-hidden rounded-xl p-12 bg-${bgColor} text-${cardTextColor} sm:min-w-[400px] sm:max-w-[400px]`}
      style={{ boxShadow: '0 0 60px 0 #5B2E4F' }}
    >
      {/* Text */}
      <div className="flex w-full flex-col gap-8 rounded-b-xl text-center">
        <p className="weight-500 text-[24px] tracking-tight lg:text-[35px]">{name}</p>
        {title && (
          <p
            className="mt-2 flex min-h-[67px] flex-col gap-6 text-[22px] leading-[1.2] lg:text-[28px]"
            data-i18n
            dangerouslySetInnerHTML={{ __html: t(title) }}
          />
        )}
      </div>
    </div>
  );
};

const MeetTheTeamImageTileItem = ({ bgColor, cardTextColor, awardCategory, imageSrc, name, title }: MeetTheTeamCarouselItem) => {
  return (
    <div
      className={`relative z-20 flex h-full min-h-[410px] w-max min-w-[325px] max-w-[325px] snap-center flex-col items-center justify-between gap-5 overflow-hidden rounded-xl px-6 py-10 bg-${bgColor} text-${cardTextColor}`}
      style={{ boxShadow: '0 0 60px 0 #5B2E4F' }}
    >
      <p className="weight-500 min-h-[60px] text-center text-[20px] tracking-[-8%]">{awardCategory}</p>

      {imageSrc && (
        <div className="h-[140px] w-[140px] shrink-0 overflow-hidden rounded-full">
          <img src={imageSrc} alt={name} className="h-full w-full object-cover" />
        </div>
      )}
      <div className="weight-300 min-h-[90px] text-center text-[20px] tracking-[-5%]">
        <p>
          {name}, {title}
        </p>
      </div>
    </div>
  );
};

const MeetTheTeam = ({
  bgColor = 'white',
  cardTextColor = 'black',
  headerContent,
  carouselItems,
  imageTiles = false,
  sectionClass = '',
  leadingGutterClass = '',
  trailingGutterClass = '',
  scrollRef,
  cta,
}: any) => {
  const { t } = useSanitizedTranslation();

  const internalScrollerRef = useRef<HTMLDivElement | null>(null);
  const scrollerRef = scrollRef || internalScrollerRef;
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout>();

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const scrollbarTrackRef = useRef<HTMLButtonElement | null>(null);

  // --- Scroll to specific card when indicator clicked
  const scrollToIndex = useCallback((index: number) => {
    const el = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!el || !card) return;

    setIsUserScrolling(true);

    const cardLeft = card.offsetLeft;
    const cardWidth = card.offsetWidth;
    const containerWidth = el.clientWidth;
    const targetScroll = cardLeft - (containerWidth - cardWidth) / 2;

    el.scrollTo({
      left: Math.max(0, targetScroll),
      behavior: 'smooth',
    });

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setIsUserScrolling(false), 600);
  }, []);

  const handleSelect = (index: number) => {
    scrollToIndex(index);
  };

  // --- Track scroll position and update indicator
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;

    let rafId = 0;

    const updateCurrentIndex = () => {
      const el = scrollerRef.current;
      if (!el) return;

      const scrollLeft = el.scrollLeft;
      const containerWidth = el.clientWidth;
      const scrollCenter = scrollLeft + containerWidth / 2;

      let closest = 0;
      let min = Infinity;

      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cardCenter = card.offsetLeft + card.offsetWidth / 2;
        const d = Math.abs(scrollCenter - cardCenter);
        if (d < min) {
          min = d;
          closest = i;
        }
      });

      if (closest !== currentIndex) setCurrentIndex(closest);
    };

    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(updateCurrentIndex);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    updateCurrentIndex();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [currentIndex, isUserScrolling]);

  // FOR DESKTOP SCROLLBAR FUNCTIONALITY
  const { scrollXProgress } = useScroll({
    container: scrollerRef,
  });

  // Update progress state when scrollXProgress changes
  useEffect(() => {
    return scrollXProgress.on('change', latest => {
      setScrollProgress(latest);
    });
  }, [scrollXProgress]);

  const handleScrollbarInteraction = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    const track = scrollbarTrackRef.current;
    const scroller = scrollerRef.current;
    if (!track || !scroller) return;

    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const trackWidth = rect.width;
    const indicatorWidth = 150;

    const maxTravel = trackWidth - indicatorWidth;
    const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

    const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
    const targetScroll = targetProgress * maxScroll;

    scroller.scrollTo({
      left: targetScroll,
      behavior: 'smooth',
    });
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      setIsDragging(true);
      handleScrollbarInteraction(e);
    },
    [handleScrollbarInteraction],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;

      const track = scrollbarTrackRef.current;
      const scroller = scrollerRef.current;
      if (!track || !scroller) return;

      const rect = track.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const trackWidth = rect.width;
      const indicatorWidth = 150;

      const maxTravel = trackWidth - indicatorWidth;
      const targetProgress = Math.max(0, Math.min(1, clickX / maxTravel));

      const maxScroll = Math.max(scroller.scrollWidth - scroller.clientWidth, 0);
      const targetScroll = targetProgress * maxScroll;

      scroller.scrollTo({
        left: targetScroll,
        behavior: 'auto',
      });
    },
    [isDragging],
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <section className={`py-16 lg:py-32 bg-${bgColor} ${sectionClass}`}>
      <div className="wrapper">
        <StackedContent center={true} noMargin={true} content={headerContent} />
      </div>

      {carouselItems?.length > 0 && (
        <>
          {/* Scrollable cards */}
          <div
            ref={scrollerRef}
            className={`hide-scroll flex w-full snap-x snap-mandatory items-start overflow-x-auto py-16 pr-12 md:snap-none md:pr-0 ${
              imageTiles ? 'gap-[35px] pr-[35px]' : 'gap-12 pr-12'
            }`}
          >
            <div className={`shrink-0 ${leadingGutterClass}`} aria-hidden />
            {carouselItems.map((item: any, index: number) => (
              <div
                key={index}
                ref={el => {
                  cardRefs.current[index] = el;
                }}
                className="shrink-0 snap-center"
                style={{
                  scrollSnapAlign: 'center',
                  scrollSnapStop: 'always',
                }}
              >
                {imageTiles ? (
                  <MeetTheTeamImageTileItem {...item} bgColor={bgColor} cardTextColor={cardTextColor} />
                ) : (
                  <MeetTheTeamCarouselItem {...item} bgColor={bgColor} cardTextColor={cardTextColor} />
                )}
              </div>
            ))}
            <div className={`shrink-0  ${trailingGutterClass}`} aria-hidden />
          </div>

          {/* Mobile croll indicators */}
          <div className="z-20 mb-8 mt-8 flex w-full justify-center gap-2 md:hidden">
            {carouselItems.map((_: any, index: number) => (
              <button
                key={index}
                onClick={() => handleSelect(index)}
                className={`h-3 w-3 rounded-full transition-all duration-300 ${currentIndex === index ? 'w-8 bg-white' : 'bg-white/30'}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Desktop scrollbar */}
          <div className="mt-4 hidden w-full justify-center pb-16 md:flex">
            <button
              ref={scrollbarTrackRef}
              onMouseDown={handleMouseDown}
              className="relative z-20 flex h-[10px] w-[750px] cursor-pointer items-center overflow-hidden rounded-full border-0 bg-[#5B2E4F] p-0"
              aria-label="Scroll carousel"
            >
              <div
                className="pointer-events-none h-[10px] w-[150px] rounded-full bg-[#DBE4E9]"
                style={{
                  transform: `translateX(${scrollProgress * 600}px)`,
                  transition: isDragging ? 'none' : 'transform 200ms ease-out',
                }}
              ></div>
            </button>
          </div>
        </>
      )}

      {cta && <StackedContent content={cta} />}
    </section>
  );
};

export default MeetTheTeam;
